import {
  Boxes,
  CircleAlert,
  PackageCheck,
  PackageX,
  RefreshCw,
  Search,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'
import {
  formatearCantidadBodega,
  obtenerEstadoStock,
} from '../../utils/bodega'

function TarjetaResumen({ icono: Icono, etiqueta, valor }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <Icono className="text-emerald-600" />

      <p className="mt-5 text-sm text-slate-500">{etiqueta}</p>

      <p className="mt-1 text-2xl font-bold text-slate-900">
        {valor}
      </p>
    </div>
  )
}

// Al entrar se muestran solo los productos con saldo mayor que cero.
const ESTADO_INICIAL = 'con_stock'

function SaldosBodega() {
  const { empresaActual } = useEmpresa()

  const [saldos, setSaldos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const [busqueda, setBusqueda] = useState('')
  const [filtroBodega, setFiltroBodega] = useState('')
  const [filtroCategoria, setFiltroCategoria] = useState('')
  const [filtroEstado, setFiltroEstado] = useState(ESTADO_INICIAL)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setSaldos([])
      setCargando(false)
      return
    }

    setFiltroBodega('')
    setFiltroCategoria('')
    setFiltroEstado(ESTADO_INICIAL)
    setBusqueda('')

    cargarSaldos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const cargarSaldos = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('vw_stock_bodega')
        .select(`
          empresa_id,

          bodega_id,
          bodega_codigo,
          bodega_nombre,
          bodega_activa,

          producto_id,
          producto_codigo,
          producto_nombre,
          producto_activo,

          categoria_id,
          categoria_nombre,

          unidad_medida_id,
          unidad_codigo,
          unidad_nombre,

          stock_minimo,
          saldo,
          stock_bajo
        `)
        .eq('empresa_id', empresaActual.id)
        .order('producto_codigo')

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setSaldos(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar saldos de bodega:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar los saldos de bodega.'
      )
      setSaldos([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const bodegasDisponibles = useMemo(() => {
    const mapa = new Map()

    saldos.forEach((saldo) => {
      if (!mapa.has(saldo.bodega_id)) {
        mapa.set(saldo.bodega_id, {
          id: saldo.bodega_id,
          codigo: saldo.bodega_codigo,
          nombre: saldo.bodega_nombre,
        })
      }
    })

    return Array.from(mapa.values()).sort((a, b) =>
      (a.codigo || '').localeCompare(b.codigo || '')
    )
  }, [saldos])

  const categoriasDisponibles = useMemo(() => {
    const mapa = new Map()

    saldos.forEach((saldo) => {
      if (saldo.categoria_id && !mapa.has(saldo.categoria_id)) {
        mapa.set(saldo.categoria_id, {
          id: saldo.categoria_id,
          nombre: saldo.categoria_nombre,
        })
      }
    })

    return Array.from(mapa.values()).sort((a, b) =>
      (a.nombre || '').localeCompare(b.nombre || '')
    )
  }, [saldos])

  const saldosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase()

    return saldos.filter((saldo) => {
      const coincideBusqueda =
        !termino ||
        (saldo.producto_codigo || '')
          .toLowerCase()
          .includes(termino) ||
        (saldo.producto_nombre || '')
          .toLowerCase()
          .includes(termino) ||
        (saldo.categoria_nombre || '')
          .toLowerCase()
          .includes(termino)

      const coincideBodega =
        !filtroBodega || saldo.bodega_id === filtroBodega

      const coincideCategoria =
        !filtroCategoria || saldo.categoria_id === filtroCategoria

      const cantidad = Number(saldo.saldo) || 0

      const coincideEstado =
        !filtroEstado ||
        (filtroEstado === 'con_stock' && cantidad > 0) ||
        (filtroEstado === 'sin_stock' && cantidad <= 0) ||
        (filtroEstado === 'stock_bajo' && saldo.stock_bajo)

      return (
        coincideBusqueda &&
        coincideBodega &&
        coincideCategoria &&
        coincideEstado
      )
    })
  }, [saldos, busqueda, filtroBodega, filtroCategoria, filtroEstado])

  const resumen = useMemo(() => {
    const productosDistintos = new Set()
    let conStock = 0
    let sinStock = 0
    let stockBajo = 0

    saldosFiltrados.forEach((saldo) => {
      productosDistintos.add(saldo.producto_id)

      const cantidad = Number(saldo.saldo) || 0

      if (cantidad > 0) {
        conStock += 1
      } else {
        sinStock += 1
      }

      if (saldo.stock_bajo) {
        stockBajo += 1
      }
    })

    return {
      productos: productosDistintos.size,
      conStock,
      sinStock,
      stockBajo,
    }
  }, [saldosFiltrados])

  const hayFiltrosActivos =
    Boolean(busqueda.trim()) ||
    Boolean(filtroBodega) ||
    Boolean(filtroCategoria) ||
    filtroEstado !== ESTADO_INICIAL

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroBodega('')
    setFiltroCategoria('')
    setFiltroEstado(ESTADO_INICIAL)
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <RefreshCw
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando saldos de bodega...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarSaldos}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (saldos.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Boxes
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No existen saldos de inventario
          </h3>

          <p className="mt-2 text-slate-500">
            Registra un saldo inicial para comenzar a ver el
            inventario disponible.
          </p>
        </div>
      )
    }

    return (
      <>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <div className="relative sm:max-w-xs sm:flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar por código, producto o categoría"
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <select
            value={filtroBodega}
            onChange={(event) => setFiltroBodega(event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todas las bodegas</option>

            {bodegasDisponibles.map((bodega) => (
              <option
                key={bodega.id}
                value={bodega.id}
              >
                {bodega.codigo} — {bodega.nombre}
              </option>
            ))}
          </select>

          <select
            value={filtroCategoria}
            onChange={(event) =>
              setFiltroCategoria(event.target.value)
            }
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todas las categorías</option>

            {categoriasDisponibles.map((categoria) => (
              <option
                key={categoria.id}
                value={categoria.id}
              >
                {categoria.nombre}
              </option>
            ))}
          </select>

          <select
            value={filtroEstado}
            onChange={(event) => setFiltroEstado(event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todos los estados</option>
            <option value="con_stock">Con stock</option>
            <option value="sin_stock">Sin stock</option>
            <option value="stock_bajo">Stock bajo</option>
          </select>
        </div>

        {saldosFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No existen saldos de inventario para los filtros
              seleccionados.
            </p>

            {hayFiltrosActivos && (
              <button
                type="button"
                onClick={limpiarFiltros}
                className="mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm lg:min-w-[1000px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Código</th>
                  <th className="px-6 py-3">Producto</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Categoría
                  </th>
                  <th className="px-6 py-3">Bodega</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Unidad
                  </th>
                  <th className="hidden px-6 py-3 text-right lg:table-cell">
                    Stock mínimo
                  </th>
                  <th className="px-6 py-3 text-right">Saldo</th>
                  <th className="px-6 py-3">Estado</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {saldosFiltrados.map((saldo) => {
                  const estado = obtenerEstadoStock(
                    saldo.saldo,
                    saldo.stock_bajo
                  )

                  return (
                    <tr
                      key={`${saldo.bodega_id}-${saldo.producto_id}`}
                    >
                      <td className="whitespace-nowrap px-6 py-4 font-semibold text-slate-900">
                        {saldo.producto_codigo}
                      </td>

                      <td className="px-6 py-4 text-slate-700">
                        {saldo.producto_nombre}

                        {!saldo.producto_activo && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                            Inactivo
                          </span>
                        )}
                      </td>

                      <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                        {saldo.categoria_nombre || 'Sin categoría'}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-slate-600">
                        {saldo.bodega_codigo} — {saldo.bodega_nombre}
                      </td>

                      <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                        {saldo.unidad_codigo}
                      </td>

                      <td className="hidden px-6 py-4 text-right text-slate-600 lg:table-cell">
                        {formatearCantidadBodega(saldo.stock_minimo)}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right font-semibold text-slate-900">
                        {formatearCantidadBodega(saldo.saldo)}{' '}
                        {saldo.unidad_codigo}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estado.clase}`}
                        >
                          {estado.etiqueta}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Bodega
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Saldos de bodega
          </h2>

          <p className="mt-2 text-slate-500">
            Inventario disponible para{' '}
            {empresaActual?.nombre_fantasia}.
          </p>
        </div>

        <button
          type="button"
          onClick={cargarSaldos}
          disabled={cargando}
          className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw
            size={18}
            className={cargando ? 'animate-spin' : ''}
          />
          Actualizar
        </button>
      </div>

      {!cargando && !error && saldos.length > 0 && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <TarjetaResumen
            icono={Boxes}
            etiqueta="Productos"
            valor={resumen.productos}
          />

          <TarjetaResumen
            icono={PackageCheck}
            etiqueta="Con stock"
            valor={resumen.conStock}
          />

          <TarjetaResumen
            icono={PackageX}
            etiqueta="Sin stock"
            valor={resumen.sinStock}
          />

          <TarjetaResumen
            icono={CircleAlert}
            etiqueta="Stock bajo"
            valor={resumen.stockBajo}
          />
        </div>
      )}

      {renderContenido()}
    </div>
  )
}

export default SaldosBodega
